import { useState } from 'react';
import { Box, Button, TextField, Typography, Alert, Select, MenuItem, FormControl, InputLabel, Paper } from '@mui/material';
import { Edit } from 'lucide-react';
import { useUpdateFund } from '@lingaai/shared-hooks';
import type { Fund } from '@lingaai/types';
import { ICON_SIZES, ICON_STROKE } from '../../../constants/icons';

const COMMON_TIMEZONES = [
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'Eastern Time (US)' },
  { value: 'America/Chicago', label: 'Central Time (US)' },
  { value: 'America/Denver', label: 'Mountain Time (US)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (US)' },
  { value: 'Europe/London', label: 'London' },
  { value: 'Europe/Paris', label: 'Paris/Berlin' },
  { value: 'Asia/Tokyo', label: 'Tokyo' },
  { value: 'Asia/Singapore', label: 'Singapore' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong' },
  { value: 'Australia/Sydney', label: 'Sydney' },
];

interface FundSettingsTabProps {
  fund: Fund;
}

export function FundSettingsTab({ fund }: FundSettingsTabProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(fund.name);
  const [abbrev, setAbbrev] = useState(fund.abbrev || '');
  const [fundNumber, setFundNumber] = useState(fund.fund_number || '');
  const [investmentStrategy, setInvestmentStrategy] = useState(fund.investment_strategy || '');
  const [reportingFrequency, setReportingFrequency] = useState<'monthly' | 'quarterly' | 'annual'>(fund.reporting_frequency || 'quarterly');
  const [reportingDay, setReportingDay] = useState(fund.reporting_day_of_month || 15);
  const [reportingTimezone, setReportingTimezone] = useState(fund.reporting_timezone || 'UTC');
  const [externalId, setExternalId] = useState(fund.external_id || '');

  const updateFund = useUpdateFund();

  const handleSave = async () => {
    try {
      await updateFund.mutateAsync({
        fundId: fund.id,
        updates: {
          name: name.trim(),
          abbrev: abbrev.trim() || null,
          fund_number: fundNumber ? Number(fundNumber) : null,
          investment_strategy: investmentStrategy.trim() || null,
          reporting_frequency: reportingFrequency,
          reporting_day_of_month: reportingDay,
          reporting_timezone: reportingTimezone,
          external_id: externalId.trim() || null,
        },
      });
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update fund:', error);
    }
  };

  const handleCancel = () => {
    setName(fund.name);
    setAbbrev(fund.abbrev || '');
    setFundNumber(fund.fund_number || '');
    setInvestmentStrategy(fund.investment_strategy || '');
    setReportingFrequency(fund.reporting_frequency || 'quarterly');
    setReportingDay(fund.reporting_day_of_month || 15);
    setReportingTimezone(fund.reporting_timezone || 'UTC');
    setExternalId(fund.external_id || '');
    setIsEditing(false);
  };

  if (!isEditing) {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">Fund Settings</Typography>
          <Button
            size="small"
            variant="contained"
            startIcon={<Edit size={ICON_SIZES.sm} strokeWidth={ICON_STROKE} />}
            onClick={() => setIsEditing(true)}
            sx={{ fontSize: '0.75rem', textTransform: 'none', py: 0.5 }}
          >
            Edit
          </Button>
        </Box>

        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Fund Name
              </Typography>
              <Typography variant="body1">{fund.name}</Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Abbreviation
              </Typography>
              <Typography variant="body1">{fund.abbrev || 'Not set'}</Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Fund Number
              </Typography>
              <Typography variant="body1">{fund.fund_number || 'Not set'}</Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Investment Strategy
              </Typography>
              <Typography variant="body1">{fund.investment_strategy || 'Not set'}</Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Reporting Frequency
              </Typography>
              <Typography variant="body1">{fund.reporting_frequency ? fund.reporting_frequency.charAt(0).toUpperCase() + fund.reporting_frequency.slice(1) : 'Not set'}</Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Reporting Day of Month
              </Typography>
              <Typography variant="body1">{fund.reporting_day_of_month || 'Not set'}</Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Reporting Timezone
              </Typography>
              <Typography variant="body1">{fund.reporting_timezone ? COMMON_TIMEZONES.find((tz) => tz.value === fund.reporting_timezone)?.label || fund.reporting_timezone : 'Not set'}</Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                External ID
              </Typography>
              <Typography variant="body1">{fund.external_id || 'Not set'}</Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 3 }}>
        Edit Fund Settings
      </Typography>

      {updateFund.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to update fund settings. Please try again.
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 600 }}>
        <TextField fullWidth label="Fund Name" value={name} onChange={(e) => setName(e.target.value)} required />

        <TextField
          fullWidth
          label="Abbreviation"
          value={abbrev}
          onChange={(e) => setAbbrev(e.target.value.toUpperCase())}
          helperText="Short code for sidebar (max 6 chars, e.g., SII, OMSC I, ABL II)"
          inputProps={{ maxLength: 10 }}
        />

        <TextField
          fullWidth
          label="Fund Number"
          type="number"
          value={fundNumber}
          onChange={(e) => setFundNumber(e.target.value)}
          helperText="Sequential number for chronological sorting (1, 2, 3, etc.)"
          inputProps={{ min: 1 }}
        />

        <TextField fullWidth label="Investment Strategy" value={investmentStrategy} onChange={(e) => setInvestmentStrategy(e.target.value)} helperText="e.g., SaaS, Real Estate, Healthcare, FinTech" />

        <FormControl fullWidth>
          <InputLabel>Reporting Frequency</InputLabel>
          <Select value={reportingFrequency} label="Reporting Frequency" onChange={(e) => setReportingFrequency(e.target.value as any)}>
            <MenuItem value="monthly">Monthly</MenuItem>
            <MenuItem value="quarterly">Quarterly</MenuItem>
            <MenuItem value="annual">Annual</MenuItem>
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="Reporting Day of Month"
          type="number"
          value={reportingDay}
          onChange={(e) => setReportingDay(Number(e.target.value))}
          inputProps={{ min: 1, max: 31 }}
          helperText="Day of the month when reports are due (1-31)"
        />

        <FormControl fullWidth>
          <InputLabel>Reporting Timezone</InputLabel>
          <Select value={reportingTimezone} label="Reporting Timezone" onChange={(e) => setReportingTimezone(e.target.value)}>
            {COMMON_TIMEZONES.map((tz) => (
              <MenuItem key={tz.value} value={tz.value}>
                {tz.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField fullWidth label="External ID" value={externalId} onChange={(e) => setExternalId(e.target.value)} helperText="Optional external system ID (e.g., Salesforce Fund ID)" />

        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <Button onClick={handleCancel} fullWidth disabled={updateFund.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" fullWidth disabled={updateFund.isPending || !name.trim()}>
            {updateFund.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
